import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Send, X, AlertTriangle, Eye, EyeOff, FileText } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const AUDIENCE_OPTIONS = [
  { value: 'registered', label: 'Registered participants' },
  { value: 'invited', label: 'Invited (not yet registered)' },
  { value: 'waitlist', label: 'Waitlist only' },
  { value: 'attended', label: 'Checked-in attendees' },
  { value: 'all', label: 'Everyone on the roster' },
];

/**
 * Phase 27.30.12 / 27.30.13 — Compose a free-form email to a filtered
 * slice of the event roster. Body is treated as HTML (organizers commonly
 * paste links + basic formatting). Templates load ready-made subject +
 * body content that use the event's cover, localised date/time, location,
 * and virtual link — the manager can freely edit before sending.
 *
 * These templates NEVER replace the transactional emails (reminder_24h,
 * registration_confirmed, event_updated, etc.) — they are strictly for
 * ad-hoc manager broadcasts.
 */
export default function NotificationComposeDialog({ open, onClose, event, onSent }) {
  const [audience, setAudience] = useState('registered');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('blank');
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const reset = () => {
    setAudience('registered');
    setSubject('');
    setBody('');
    setTemplateId('blank');
    setPreview(false);
  };

  useEffect(() => {
    if (!open || !event?.event_id) return;
    setTemplatesLoading(true);
    calendarAPI.notificationTemplates(event.event_id)
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [open, event?.event_id]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    if (id === 'blank') {
      setSubject('');
      setBody('');
      return;
    }
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    // Confirm overwrite if user already typed something.
    if ((subject.trim() || body.trim())
        && !window.confirm('Load this template? Any text you typed will be replaced.')) {
      return;
    }
    setSubject(t.subject || '');
    setBody(t.body_html || '');
  };

  const handleClose = () => {
    if (sending) return;
    reset();
    onClose?.();
  };

  const submit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    if (!window.confirm(
      `Send this email to the "${AUDIENCE_OPTIONS.find((a) => a.value === audience)?.label}" audience?`,
    )) return;

    setSending(true);
    try {
      const { data } = await calendarAPI.notificationCompose(event.event_id, {
        subject: subject.trim(),
        body_html: body,
        audience,
      });
      const parts = [
        `${data.sent || 0} sent`,
        (data.failed || 0) > 0 ? `${data.failed} failed` : null,
        (data.skipped || 0) > 0 ? `${data.skipped} skipped` : null,
      ].filter(Boolean).join(' · ');
      toast.success(`Custom broadcast: ${parts}`);
      reset();
      onSent?.(data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="notification-compose-dialog">
        <DialogHeader>
          <DialogTitle>Compose event email</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              This sends immediately to every participant matching the
              audience filter. Emails are HTML — links, bold, and line
              breaks are supported. Templates never replace the automatic
              reminders / confirmations / update notices for this event.
            </div>
          </div>

          {/* Template picker */}
          <div>
            <Label htmlFor="compose-template" className="text-xs flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Start from a template
            </Label>
            <Select
              value={templateId}
              onValueChange={applyTemplate}
              disabled={sending || templatesLoading}
            >
              <SelectTrigger id="compose-template" className="mt-1" data-testid="compose-template">
                <SelectValue placeholder={templatesLoading ? 'Loading templates…' : 'Blank (start from scratch)'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">Blank — start from scratch</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Templates pre-fill the cover banner, event date, location, and
              virtual link. Everything is editable below before you send.
            </p>
          </div>

          <div>
            <Label htmlFor="compose-audience" className="text-xs">Recipients</Label>
            <Select value={audience} onValueChange={setAudience} disabled={sending}>
              <SelectTrigger id="compose-audience" className="mt-1" data-testid="compose-audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIENCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="compose-subject" className="text-xs">Subject</Label>
            <Input
              id="compose-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Update about your upcoming event"
              className="mt-1"
              disabled={sending}
              maxLength={200}
              data-testid="compose-subject"
            />
            <div className="text-[10px] text-slate-400 mt-0.5">{subject.length} / 200</div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="compose-body" className="text-xs">
                Message (HTML supported)
              </Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setPreview((v) => !v)}
                disabled={sending || !body.trim()}
                className="h-7 text-[11px]"
                data-testid="compose-preview-toggle"
              >
                {preview
                  ? (<><EyeOff className="h-3.5 w-3.5 mr-1" /> Show source</>)
                  : (<><Eye className="h-3.5 w-3.5 mr-1" /> Preview</>)}
              </Button>
            </div>
            {preview ? (
              <div
                className="border border-slate-200 rounded p-3 bg-slate-50 max-h-96 overflow-y-auto"
                data-testid="compose-preview"
              >
                {/* Rendered preview — trusted since it was authored by the manager */}
                <div dangerouslySetInnerHTML={{ __html: body }} />
              </div>
            ) : (
              <Textarea
                id="compose-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi team,&#10;&#10;A friendly reminder that our meeting starts at&#10;10 AM. Please review the attached agenda ahead of time.&#10;&#10;See you soon!"
                rows={12}
                className="mt-1 font-mono text-xs"
                disabled={sending}
                maxLength={20000}
                data-testid="compose-body"
              />
            )}
            <div className="text-[10px] text-slate-400 mt-0.5">{body.length} / 20000</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={sending} data-testid="compose-cancel">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button onClick={submit} disabled={sending} data-testid="compose-send">
            <Send className="h-4 w-4 mr-1" /> {sending ? 'Sending…' : 'Send now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
